import type { CompRef, ValueOf } from '@wix/document-services-types';
import type { Presets } from '@wix/component-protocol/schema';
import { OperationSource } from '../../panorama';

import type { ManifestComponentExtensionType } from '../../stores';
import type { ContainerLayoutTransitionConfig } from '../../types';
import type { ManifestQueryAPI } from '../../handlers';
import { DependencyToken } from '../../dependencies';
import type {
  VirtualElement,
  VirtualElementAPIType,
} from '../providers/VirtualElement';
import type { DependenciesProvider } from '../providers/DependenciesProvider';
import type { EditorPointer } from '@wix/editor-document-logic-api';

type ArgSerializer<T = unknown> = (
  arg: T,
) => Record<string, unknown> | undefined;

type Serializers<TArgs extends unknown[]> = {
  [K in keyof TArgs]?: ArgSerializer<TArgs[K]>;
};

const serialize =
  (key: string): ArgSerializer =>
  (arg) => ({ [key]: arg });

const OperationName = {
  SET_STYLES: 'set-styles',
  APPLY_COMPONENT_RESIZE: 'apply-component-resize',
  REGENERATE_CONTAINER_LAYOUT: 'regenerate-container-layout',
  APPLY_PRESET: 'apply-preset',
};

const OPERATION_SERIALIZERS: Record<string, Serializers<unknown[]>> = {
  [OperationName.SET_STYLES]: [
    serialize('editor_pointer'),
    serialize('extension_type'),
    undefined,
  ],
  [OperationName.APPLY_COMPONENT_RESIZE]: [
    serialize('editor_pointer'),
    serialize('target_size'),
  ],
  [OperationName.REGENERATE_CONTAINER_LAYOUT]: [
    serialize('editor_pointer'),
    serialize('container_layout_transition_config'),
  ],
  [OperationName.APPLY_PRESET]: [
    serialize('editor_pointer'),
    serialize('extension_type'),
    serialize('preset_key'),
  ],
};

export class ManifestQueryOperationTracker {
  constructor(
    private readonly node: VirtualElement<
      | VirtualElementAPIType.ManifestQuery
      | VirtualElementAPIType.ManifestQueryReadonly
    >,
    private readonly dependenciesProvider: DependenciesProvider,
    private readonly api: ManifestQueryAPI,
  ) {}

  #extraContext(): Record<string, string> {
    const operationContext = this.node.getOperationalContext();
    const virtualManifest = this.node.getVirtualManifest();
    return {
      origin_id:
        operationContext?.currentOperationId ??
        virtualManifest.virtualManifestId,
      component_type: this.node.getComponentType(),
      source: operationContext?.currentSource ?? OperationSource.UNKNOWN,
    };
  }

  #track<A extends [EditorPointer<CompRef>, ...unknown[]]>(
    func: (...args: A) => void,
    methodName: string,
    args: [...A],
  ) {
    const trackTransaction = this.dependenciesProvider.get(
      DependencyToken.PANORAMA_TRACK_TRANSACTION,
    );

    if (trackTransaction) {
      return trackTransaction(
        func,
        methodName,
        args,
        OPERATION_SERIALIZERS[methodName] as Serializers<A>,
        this.#extraContext(),
      );
    } else {
      return func(...args);
    }
  }

  setStyles(
    editorPointer: EditorPointer<CompRef>,
    extensionType: ManifestComponentExtensionType,
    styles: {
      cssProperties: Record<string, string>;
      cssCustomProperties: Record<string, string>;
    },
  ) {
    return this.#track(
      this.api.styles.setStyles.bind(this.api.styles),
      OperationName.SET_STYLES,
      [editorPointer, extensionType, styles],
    );
  }

  applyComponentResize(
    editorPointer: EditorPointer<CompRef>,
    targetSize: ValueOf<Presets>['initialSize'],
  ) {
    return this.#track(
      this.api.layout.applyComponentResize.bind(this.api.layout),
      OperationName.APPLY_COMPONENT_RESIZE,
      [editorPointer, targetSize],
    );
  }

  regenerateContainerLayout(
    editorPointer: EditorPointer<CompRef>,
    containerLayoutTransitionConfig: ContainerLayoutTransitionConfig,
  ) {
    return this.#track(
      this.api.layout.regenerateContainerLayout.bind(this.api.layout),
      OperationName.REGENERATE_CONTAINER_LAYOUT,
      [editorPointer, containerLayoutTransitionConfig],
    );
  }

  applyPreset(
    editorPointer: EditorPointer<CompRef>,
    extensionType: ManifestComponentExtensionType,
    presetKey: string,
  ) {
    return this.#track(
      this.api.presets.applyPreset.bind(this.api.presets),
      OperationName.APPLY_PRESET,
      [editorPointer, extensionType, presetKey],
    );
  }
}
